import {
  Box,
  Flex,
  Icon,
  Button,
  Select,
  Table,
  Tbody,
  Text,
  Th,
  Thead,
  Tr,
  Td,
  useColorModeValue,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  HStack,
  IconButton,
  Avatar,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  SimpleGrid,
  VStack,
  Img,
} from '@chakra-ui/react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
// Custom components
import Card from 'components/card/Card';
import * as React from 'react';
import * as XLSX from 'xlsx';
// Icons
import { MdCheckCircle, MdCancel, MdAccessTime, MdError, MdSearch, MdArrowDropDown, MdSettings, MdViewModule, MdViewList, MdUnfoldMore, MdUnfoldLess, MdChevronLeft, MdChevronRight, MdClose, MdAdd } from 'react-icons/md';
import { usePermissions } from 'hooks/usePermissions';
import plusSquaredIcon from "assets/img/icons/Dashboard-icon/plus-squared.png"

const columnHelper = createColumnHelper();

export default function ComplexTable(props) {
  const { hasPermission } = usePermissions();
  const { tableData, searchQuery, onSearchChange, onStatusFilter, onSortChange, statusFilter, sortBy, onAddClick, viewMode, setViewMode } = props;
  const [sorting, setSorting] = React.useState([]);
  const [pagination, setPagination] = React.useState({
    pageIndex: 0,
    pageSize: 20,
  });

  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const bgColor = useColorModeValue('white', 'gray.800');

  const getStatusBadgeColor = (statut) => {
    switch (statut) {
      case 'terminée':
        return { bg: "#EDF7F1", border: "#CDE8D9", text: "#006133", label: "Terminée" };
      case 'en cours':
        return { bg: "#FFF9E7", border: "#FFE899", text: "#8D6E00", label: "En cours" };
      case 'créée':
      case 'En attente':
        return { bg: "#E3F2FD", border: "#CDE8D9", text: "#2196F3", label: "Créée" };
      case 'annulée':
        return { bg: "#FDF2F2", border: "#FBD5D5", text: "#9B1C1C", label: "Annulée" };
      case 'dérangement':
        return { bg: "#FDF2F2", border: "#FBD5D5", text: "#9B1C1C", label: "Dérangement" };
      default:
        return { bg: "#EDF7F1", border: "#CDE8D9", text: "#006133", label: statut };
    }
  };

  const getUrgenceBadgeColor = (etat) => {
    switch (etat) {
      case 'urgente':
        return { bg: '#FFEEF3', border: 'rgba(248,40,90,0.2)', text: '#F8285A' };
      case 'planifiée':
        return { bg: '#E3F2FD', border: 'rgba(33,150,243,0.2)', text: '#2196F3' };
      case 'Moyenne':
        return { bg: '#FFF4E6', border: 'rgba(255,193,7,0.2)', text: '#FFC107' };
      case 'Normale':
        return { bg: '#EAF8FA', border: 'rgba(41,186,205,0.2)', text: '#29BACD' };
      default:
        return { bg: '#F5F5F5', border: 'rgba(0,0,0,0.1)', text: '#656C7A' };
    }
  };

  // Fonction pour capitaliser la première lettre de chaque mot
  const capitalizeWords = (str) => {
    if (!str) return '';
    // Conserver les textes déjà en majuscules complètes
    if (str === str.toUpperCase()) {
      return str;
    }
    return str
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    // Si c'est déjà une string formatée, la retourner telle quelle
    if (typeof dateString === 'string' && dateString.includes('/')) {
      return dateString;
    }
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (e) {
      return dateString;
    }
  };

  const getExportFileDate = () => {
    const now = new Date();
    return now.toISOString().slice(0, 10);
  };

  const buildExportRows = React.useCallback(() => {
    return (tableData || []).map((row) => {
      const reference = row.bon_de_commande && row.bon_de_commande.toString().trim() !== ''
        ? row.bon_de_commande
        : (row.id ?? '');

      return {
        "Référence": reference || 'N/A',
        "Nom du Patient": row.patient_name || 'N/A',
        "Intervenant": row.ambulancier || 'N/A',
        "Type Ambulance": row.type_ambulance && row.type_ambulance !== '-' ? row.type_ambulance : 'N/A',
        "État d'Urgence": row.etat_mission || 'N/A',
        "Date de création": row.créé_le ? formatDate(row.créé_le) : 'N/A',
        "Date de mission": row.date_mission ? formatDate(row.date_mission) : 'N/A',
        "Statut": row.statut || 'N/A',
        "Numéro": row.num || 'N/A',
      };
    });
  }, [tableData]);

  const exportToCsv = () => {
    const rows = buildExportRows();
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Missions');
    XLSX.writeFile(workbook, `missions_${getExportFileDate()}.csv`, { bookType: 'csv' });
  };

  const exportToExcel = () => {
    const rows = buildExportRows();
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Missions');
    XLSX.writeFile(workbook, `missions_${getExportFileDate()}.xlsx`, { bookType: 'xlsx' });
  };

  const headerTextProps = {
    fontSize: "12px",
    fontWeight: "500",
    color: "#455879",
    fontFamily: "Poppins, sans-serif",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  };

  const columns = [
    columnHelper.accessor('bon_de_commande', {
      id: 'bon_de_commande',
      header: () => (
        <Flex align="center" gap="4px">
          <Text {...headerTextProps}>Référence</Text>
          <Icon as={MdUnfoldMore} w="14px" h="14px" color="#455879" />
        </Flex>
      ),
      cell: (info) => (
        <Text fontSize="14px" fontWeight="400" color="#455879" fontFamily="Poppins, sans-serif">
          {(() => {
            const refValue = info.getValue();
            const hasReference = refValue && refValue.toString().trim() !== '';
            if (hasReference) {
              return refValue;
            }
            const missionId = info.row.original?.id;
            return missionId || 'N/A';
          })()}
        </Text>
      ),
      size: 98,
    }),
    columnHelper.accessor('patient_name', {
      id: 'patient_name',
      header: () => (
        <Flex align="center" gap="4px">
          <Text {...headerTextProps}>Nom du Patient</Text>
          <Icon as={MdUnfoldMore} w="14px" h="14px" color="#455879" />
        </Flex>
      ),
      cell: (info) => (
        <Text fontSize="14px" fontWeight="400" color="#455879" fontFamily="Poppins, sans-serif">
          {info.getValue() || 'N/A'}
        </Text>
      ),
      size: 200,
    }),
    columnHelper.accessor('ambulancier', {
      id: 'ambulancier',
      header: () => (
        <Flex align="center" gap="4px">
          <Text {...headerTextProps}>Intervenant</Text>
          <Icon as={MdUnfoldMore} w="14px" h="14px" color="#455879" />
        </Flex>
      ),
      cell: (info) => {
        const name = info.getValue() || 'N/A';
        return (
          <Text fontSize="14px" fontWeight="400" color="#455879" fontFamily="Poppins, sans-serif">
            {name}
          </Text>
        );
      },
      size: 213,
    }),
    columnHelper.accessor('type_ambulance', {
      id: 'type_ambulance',
      header: () => (
        <Flex align="center" gap="4px">
          <Text {...headerTextProps}>Type Ambulance</Text>
          <Icon as={MdUnfoldMore} w="14px" h="14px" color="#455879" />
        </Flex>
      ),
      cell: (info) => {
        const value = info.getValue();
        const displayValue = !value || value === '-' || value === 'N/A' ? "Soins d'urgence" : value;
        return (
          <Text fontSize="14px" fontWeight="400" color="#455879" fontFamily="Poppins, sans-serif">
            {capitalizeWords(displayValue)}
          </Text>
        );
      },
      size: 298,
    }),
    columnHelper.accessor('num', {
      id: 'num',
      header: () => (
        <Flex align="center" gap="4px">
          <Text {...headerTextProps}>Numéro</Text>
          <Icon as={MdUnfoldMore} w="14px" h="14px" color="#455879" />
        </Flex>
      ),
      cell: (info) => (
        <Text fontSize="14px" fontWeight="400" color="#455879" fontFamily="Poppins, sans-serif">
          {info.getValue() || 'N/A'}
        </Text>
      ),
      size: 166,
    }),
    columnHelper.accessor('etat_mission', {
      id: 'etat_mission',
      header: () => (
        <Flex align="center" gap="4px">
          <Text {...headerTextProps}>État d'Urgence</Text>
          <Icon as={MdUnfoldMore} w="14px" h="14px" color="#455879" />
        </Flex>
      ),
      cell: (info) => {
        const etat = info.getValue() || 'Normale';
        const colors = getUrgenceBadgeColor(etat);
        return (
          <Badge
            bg={colors.bg}
            border={`1px solid ${colors.border}`}
            borderRadius="4px"
            p="6px 10px"
            display="flex"
            alignItems="center"
            gap="6px"
            w="fit-content"
          >
            <Box w="6px" h="6px" borderRadius="50%" bg={colors.text} />
            <Text fontSize="12px" fontWeight="500" color={colors.text} fontFamily="Poppins, sans-serif">
              {capitalizeWords(etat)}
            </Text>
          </Badge>
        );
      },
      size: 154,
    }),
    columnHelper.accessor('créé_le', {
      id: 'créé_le',
      header: () => (
        <Flex align="center" gap="4px">
          <Text {...headerTextProps}>Date de création</Text>
          <Icon as={MdUnfoldMore} w="14px" h="14px" color="#455879" />
        </Flex>
      ),
      cell: (info) => {
        const value = info.getValue();
        return (
          <Text fontSize="14px" fontWeight="400" color="#455879" fontFamily="Poppins, sans-serif">
            {value ? formatDate(value) : 'N/A'}
          </Text>
        );
      },
      size: 168,
    }),
    columnHelper.accessor('date_mission', {
      id: 'date_mission',
      header: () => (
        <Flex align="center" gap="4px">
          <Text {...headerTextProps}>Date de mission</Text>
          <Icon as={MdUnfoldMore} w="14px" h="14px" color="#455879" />
        </Flex>
      ),
      cell: (info) => (
        <Text fontSize="14px" fontWeight="400" color="#455879" fontFamily="Poppins, sans-serif">
          {formatDate(info.getValue())}
        </Text>
      ),
      size: 164,
    }),
    columnHelper.accessor('statut', {
      id: 'statut',
      header: () => (
        <Flex align="center" gap="4px">
          <Text {...headerTextProps}>Statut</Text>
          <Icon as={MdUnfoldMore} w="14px" h="14px" color="#455879" />
        </Flex>
      ),
      cell: (info) => {
        const statut = info.getValue() || 'New';
        const colors = getStatusBadgeColor(statut);
        return (
          <HStack
            spacing={2}
            bg={colors.bg}
            border="1px solid"
            borderColor={colors.border}
            px={3}
            py={1}
            borderRadius="full"
            w="fit-content"
            align="center"
          >
            <Box w="8px" h="8px" borderRadius="full" bg={colors.text} />
            <Text fontSize="13px" fontWeight="600" color={colors.text} fontFamily="Poppins, sans-serif">
              {colors.label || capitalizeWords(statut)}
            </Text>
          </HStack>
        );
      },
      size: 129,
    }),
    columnHelper.accessor('actions', {
      id: 'actions',
      header: () => null,
      cell: (info) => (
        <Flex justify="center" align="center" w="32px" h="32px">
          {info.getValue()}
        </Flex>
      ),
      size: 52,
    })
  ];

  const table = useReactTable({
    data: tableData,
    columns,
    state: {
      sorting,
      pagination,
    },
    onSortingChange: setSorting,
    onPaginationChange: setPagination,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    autoResetPageIndex: false,
  });

  const startIndex = pagination.pageIndex * pagination.pageSize + 1;
  const endIndex = Math.min((pagination.pageIndex + 1) * pagination.pageSize, tableData.length);
  const totalItems = tableData.length;

  return (
    <Card flexDirection="column" w="100%" px="0px" bg={bgColor} position="relative" zIndex={1}>
      {/* Header avec recherche et filtres */}
      <Flex
        px="20px"
        py="25px"
        justifyContent="space-between"
        align="center"
        borderBottom="1px solid #E5E5E5"
      >
        <Text fontSize="14px" fontWeight="400" color="#455879" fontFamily="Poppins, sans-serif">
          Liste des missions
        </Text>
        <Flex gap="10px" align="center">
          {/* Barre de recherche */}
          <InputGroup w="240px" h="36px">
            <InputLeftElement pointerEvents="none" h="36px">
              <Icon as={MdSearch} w="16px" h="16px" color="#656C7A" />
            </InputLeftElement>
            <Input
              placeholder="Recherche d'une mission..."
              value={searchQuery || ''}
              onChange={(e) => onSearchChange && onSearchChange(e.target.value)}
              h="36px"
              fontSize="12px"
              fontFamily="Poppins, sans-serif"
              bg="white"
              border="1px solid #E5E5E5"
              borderRadius="6px"
              _focus={{ borderColor: '#459CE9', boxShadow: 'none' }}
            />
          </InputGroup>

          {/* Filtre Statut */}
          {statusFilter && (
            <HStack
              bg="white"
              border="1px solid #E5E5E5"
              borderRadius="6px"
              px="10px"
              py="11px"
              h="36px"
              spacing="6px"
            >
              <Text fontSize="12px" fontWeight="500" color="#455879" fontFamily="Poppins, sans-serif">
                Statut:
              </Text>
              <Text fontSize="14px" fontWeight="500" color="#455879" fontFamily="Poppins, sans-serif">
                {statusFilter}
              </Text>
              {onStatusFilter && (
                <IconButton
                  icon={<MdClose />}
                  size="xs"
                  variant="ghost"
                  onClick={() => onStatusFilter('')}
                  aria-label="Supprimer filtre"
                />
              )}
            </HStack>
          )}

          {/* Filtre Trier par */}
          {sortBy && (
            <HStack
              bg="white"
              border="1px solid #E5E5E5"
              borderRadius="6px"
              px="10px"
              py="11px"
              h="36px"
              spacing="6px"
            >
              <Text fontSize="12px" fontWeight="500" color="#455879" fontFamily="Poppins, sans-serif">
                Trier par:
              </Text>
              <Text fontSize="14px" fontWeight="500" color="#455879" fontFamily="Poppins, sans-serif">
                {sortBy}
              </Text>
              {onSortChange && (
                <IconButton
                  icon={<MdClose />}
                  size="xs"
                  variant="ghost"
                  onClick={() => onSortChange('')}
                  aria-label="Supprimer tri"
                />
              )}
            </HStack>
          )}


          {/* Bouton Export */}
          <Menu>
            <MenuButton
              as={Button}
              bg="white"
              border="1px solid #E5E5E5"
              borderRadius="6px"
              h="36px"
              px="10px"
              py="11px"
              fontSize="14px"
              fontWeight="500"
              color="#455879"
              fontFamily="Poppins, sans-serif"
              rightIcon={<Icon as={MdArrowDropDown} w="14px" h="14px" />}
            >
              Export
            </MenuButton>
            <MenuList>
              <MenuItem onClick={exportToCsv}>Exporter en CSV</MenuItem>
              <MenuItem onClick={exportToExcel}>Exporter en Excel</MenuItem>
            </MenuList>
          </Menu>



          {/* Boutons de vue */}
          <HStack spacing="4px">
            <IconButton
              icon={<MdViewModule />}
              aria-label="Vue grille"
              variant={viewMode === 'grid' ? 'solid' : 'outline'}
              bg={viewMode === 'grid' ? '#006133' : 'transparent'}
              color={viewMode === 'grid' ? 'white' : '#006133'}
              border="1px solid"
              borderColor="#006133"
              borderRadius="6px"
              w="28px"
              h="28px"
              _hover={{
                bg: viewMode === 'grid' ? '#004d26' : '#e6f4ee'
              }}
              onClick={() => setViewMode && setViewMode('grid')}
            />

            <IconButton
              icon={<MdViewList />}
              aria-label="Vue liste"
              variant={viewMode === 'list' ? 'solid' : 'outline'}
              bg={viewMode === 'list' ? '#006133' : 'transparent'}
              color={viewMode === 'list' ? 'white' : '#006133'}
              border="1px solid"
              borderColor="#006133"
              borderRadius="6px"
              w="28px"
              h="28px"
              _hover={{
                bg: viewMode === 'list' ? '#004d26' : '#e6f4ee'
              }}
              onClick={() => setViewMode && setViewMode('list')}
            />
          </HStack>


        </Flex>
      </Flex>

      {/* Tableau ou Grille selon viewMode */}
      {viewMode === 'grid' ? (
        <Box px="20px" py="20px">
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
            {table.getRowModel().rows.map((row) => {
              const mission = row.original;
              const status = mission.statut || 'créée';
              const colors = getStatusBadgeColor(status);

              return (
                <Card
                  key={mission.id}
                  bg="white"
                  borderRadius="12px"
                  p={4}
                  border="1px solid"
                  borderColor="gray.200"
                  _hover={{ shadow: 'md' }}
                >
                  <VStack align="stretch" spacing={3}>
                    <Flex justify="space-between" align="start">
                      <HStack spacing={3} flex={1}>
                        <Avatar size="md" name={mission.ambulancier || 'Intervenant'} />
                        <VStack align="start" spacing={0} flex={1}>
                          <Text fontWeight="semibold" fontSize="sm" color="gray.900" noOfLines={1}>
                            {mission.ambulancier || 'Intervenant inconnu'}
                          </Text>
                          <Text fontSize="xs" color="gray.500">
                            #{mission.bon_de_commande || mission.id} - {formatDate(mission.créé_le)}
                          </Text>
                        </VStack>
                      </HStack>
                      <Box>
                        {flexRender(row.getVisibleCells().find(c => c.column.id === 'actions')?.column.columnDef.cell, row.getVisibleCells().find(c => c.column.id === 'actions')?.getContext())}
                      </Box>
                    </Flex>

                    <HStack spacing={2}>
                      <Badge
                        bg={colors.bg}
                        border={`1px solid ${colors.border}`}
                        borderRadius="4px"
                        p="4px 8px"
                        display="flex"
                        alignItems="center"
                        gap="4px"
                      >
                        <Box w="6px" h="6px" borderRadius="50%" bg={colors.text} />
                        <Text fontSize="10px" fontWeight="500" color={colors.text}>
                          {capitalizeWords(status)}
                        </Text>
                      </Badge>
                      <Text fontSize="xs" fontWeight="bold" color="gray.600">
                        {capitalizeWords(mission.type_mission || 'Mission')}
                      </Text>
                    </HStack>

                    <Text fontSize="sm" color="gray.700" noOfLines={2}>
                      {mission.cause || 'Aucune description'}
                    </Text>

                    <Flex justify="space-between" align="center">
                      <HStack spacing={1}>
                        <Icon as={MdAccessTime} color="gray.400" w="14px" h="14px" />
                        <Text fontSize="xs" color="gray.500">{formatDate(mission.date_mission)}</Text>
                      </HStack>
                      <Badge variant="outline" colorScheme={mission.etat_mission === 'urgente' ? 'red' : 'blue'} fontSize="10px">
                        {mission.etat_mission}
                      </Badge>
                    </Flex>
                  </VStack>
                </Card>
              );
            })}
          </SimpleGrid>
        </Box>
      ) : (
        <Box px="20px" pb="20px">
          <Box
            border="1px solid"
            borderColor="gray.200"
            borderRadius="12px"
            overflow="hidden"
          >
            <Box overflowX="auto">
              <Table
                variant="simple"
                minW="1200px"
                sx={{
                  'th, td': {
                    borderRight: '1px solid',
                    borderBottom: '1px solid',
                    borderColor: 'gray.200',
                    whiteSpace: 'nowrap'
                  },
                  'th:last-child, td:last-child': { borderRight: 'none' },
                  'tr:last-child td': { borderBottom: 'none' },
                  'thead tr': { bg: 'gray.50' }
                }}
              >
                <Thead>
                  {table.getHeaderGroups().map((headerGroup) => (
                    <Tr key={headerGroup.id}>
                      {headerGroup.headers.map((header) => (
                        <Th
                          key={header.id}
                          colSpan={header.colSpan}
                          px="20px"
                          h="50px"
                          cursor={header.column.getCanSort() ? "pointer" : "default"}
                          onClick={header.column.getToggleSortingHandler()}
                          w={header.getSize()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </Th>
                      ))}
                    </Tr>
                  ))}
                </Thead>
                <Tbody>
                  {table.getRowModel().rows.map((row) => (
                    <Tr key={row.id} _hover={{ bg: 'gray.50' }}>
                      {row.getVisibleCells().map((cell) => (
                        <Td
                          key={cell.id}
                          fontSize="14px"
                          px="20px"
                          h="60px"
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Td>
                      ))}
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>
        </Box>
      )}

      {/* Pagination */}
      <Flex
        justify="space-between"
        align="center"
        px="20px"
        py="14px"
        borderTop="1px solid #E5E5E5"
      >
        <HStack spacing="10px">
          <Text fontSize="14px" fontWeight="400" color="#455879" fontFamily="Poppins, sans-serif">
            Afficher
          </Text>
          <Select
            value={pagination.pageSize}
            onChange={(e) => {
              setPagination(prev => ({ ...prev, pageSize: Number(e.target.value), pageIndex: 0 }));
            }}
            w="58px"
            h="30px"
            fontSize="12px"
            fontFamily="Poppins, sans-serif"
            border="1px solid #E5E5E5"
            borderRadius="6px"
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </Select>
          <Text fontSize="14px" fontWeight="400" color="#455879" fontFamily="Poppins, sans-serif">
            par page
          </Text>
        </HStack>

        <HStack spacing="10px">
          <Text fontSize="14px" fontWeight="400" color="#455879" fontFamily="Poppins, sans-serif">
            {startIndex} à {endIndex} sur {totalItems}
          </Text>
          <HStack spacing="2px">
            <IconButton
              icon={<MdChevronLeft />}
              aria-label="Page précédente"
              size="sm"
              variant="ghost"
              onClick={() => table.previousPage()}
              isDisabled={!table.getCanPreviousPage()}
              w="30px"
              h="30px"
              borderRadius="6px"
            />
            {Array.from({ length: Math.min(5, table.getPageCount()) }, (_, i) => {
              const pageIndex = pagination.pageIndex;
              const totalPages = table.getPageCount();
              let pageNum;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (pageIndex < 3) {
                pageNum = i + 1;
              } else if (pageIndex > totalPages - 4) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = pageIndex - 1 + i;
              }
              return (
                <Button
                  key={pageNum}
                  size="sm"
                  variant={pageNum === pageIndex + 1 ? "solid" : "ghost"}
                  onClick={() => table.setPageIndex(pageNum - 1)}
                  w="30px"
                  h="30px"
                  fontSize="14px"
                  fontWeight={pageNum === pageIndex + 1 ? "600" : "400"}
                  color={pageNum === pageIndex + 1 ? "white" : "#455879"}
                  bg={pageNum === pageIndex + 1 ? "#459CE9" : "transparent"}
                  borderRadius="6px"
                  _hover={{ bg: pageNum === pageIndex + 1 ? "#3a8bd4" : "#F8F9FA" }}
                >
                  {pageNum}
                </Button>
              );
            })}
            <IconButton
              icon={<MdChevronRight />}
              aria-label="Page suivante"
              size="sm"
              variant="ghost"
              onClick={() => table.nextPage()}
              isDisabled={!table.getCanNextPage()}
              w="30px"
              h="30px"
              borderRadius="6px"
            />
          </HStack>
        </HStack>
      </Flex>
    </Card>
  );
}
